/**
 * Who is signed in, and the one rule about who is allowed to stay.
 *
 * The allowlist check here is a courtesy, not a control. The API refuses every admin route to a
 * non-operator with a 404 regardless of what this component believes, and that refusal is the
 * security boundary. This exists so somebody who signs in with an ordinary electrician's account
 * gets told plainly, instead of being handed a portal where every screen quietly fails to load.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import * as api from '../api/client';
import { clearSession, loadSession, type AdminUser, type StoredSession } from './session';

interface AuthValue {
  user: AdminUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Thrown at a valid login by somebody who is not an operator. */
export class NotAnOperatorError extends Error {
  constructor(email: string) {
    super(`${email} is a valid account, but not an operator of the platform.`);
    this.name = 'NotAnOperatorError';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await api.login(email, password);
    if (!next.user.is_platform_admin) {
      // Signed in successfully, and still has no business here. The credentials are discarded
      // rather than kept: leaving a working session behind for an account that cannot use any of
      // it is how you end up with a portal that looks broken instead of one that says no.
      clearSession();
      throw new NotAnOperatorError(next.user.email);
    }
    setSession(next);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setSession(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user: session?.user ?? null, signIn, signOut }),
    [session, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider.');
  return value;
}
